import type { InitialUtm } from '@/api/core-api/types.gen';
import type { UtmSearch, UtmSearchKey } from '@/schemas/utm';

import {
  pickUtmValues,
  toInitialUtm,
  UTM_SEARCH_KEYS,
  clampUtmValue,
} from '@/schemas/utm';

const STORAGE_KEY = 'respondent:participantUtm';
const SCHEMA_VERSION = 1;
/** Standard first-touch style window for campaign attribution. */
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

type StoredEnvelope = {
  v: number;
  expiresAt: number;
  utm: Partial<Record<UtmSearchKey, string>>;
};

function isBrowser(): boolean {
  return globalThis.localStorage !== undefined;
}

function loadValidStoredUtm(): Partial<Record<UtmSearchKey, string>> {
  if (!isBrowser()) {
    return {};
  }
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed = JSON.parse(raw) as StoredEnvelope;
    if (
      parsed.v !== SCHEMA_VERSION ||
      typeof parsed.expiresAt !== 'number' ||
      typeof parsed.utm !== 'object' ||
      parsed.utm === null
    ) {
      globalThis.localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    if (Date.now() > parsed.expiresAt) {
      globalThis.localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return pickUtmValues(parsed.utm);
  } catch {
    try {
      globalThis.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    return {};
  }
}

/**
 * Merges non-empty UTM params from the URL into localStorage. URL values win
 * per key; keys only present in storage are kept. Refreshes the expiry window
 * whenever the URL contributes at least one UTM field.
 */
export function captureParticipantUtmFromSearch(
  search: UtmSearch | undefined,
): void {
  if (!search || !isBrowser()) {
    return;
  }
  const fromUrl = pickUtmValues(search);
  if (Object.keys(fromUrl).length === 0) {
    return;
  }
  const existing = loadValidStoredUtm();
  const merged: Partial<Record<UtmSearchKey, string>> = {
    ...existing,
    ...fromUrl,
  };
  try {
    globalThis.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        v: SCHEMA_VERSION,
        expiresAt: Date.now() + TTL_MS,
        utm: merged,
      } satisfies StoredEnvelope),
    );
  } catch {
    // private mode, quota, etc.
  }
}

/**
 * UTM fields for TanStack Router `search` props: URL first, then non-expired
 * localStorage. Omits keys with no value so URLs stay tidy.
 */
export function getParticipantUtmSearchForRouter(
  search: UtmSearch | null | undefined,
): UtmSearch {
  const stored = loadValidStoredUtm();
  const out: UtmSearch = {};
  for (const key of UTM_SEARCH_KEYS) {
    const fromUrl = clampUtmValue(search?.[key]);
    const value = fromUrl ?? stored[key];
    if (value) {
      out[key] = value;
    }
  }
  return out;
}

export function hasParticipantReferralAdvertUtm(
  search: UtmSearch | null | undefined,
): boolean {
  return getParticipantUtmSearchForRouter(search).utm_advert_id !== undefined;
}

export function getResolvedParticipantInitialUtm(
  search: UtmSearch | null | undefined,
): InitialUtm | undefined {
  return toInitialUtm(getParticipantUtmSearchForRouter(search));
}

export function clearParticipantUtmStorage(): void {
  if (!isBrowser()) {
    return;
  }
  try {
    globalThis.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
